import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import awsConfig from "../aws-config";
import { resolveAuthToken } from "../utils/authToken";
import "./FitnessBookingTemplatePage.css";

const GET_MENU_API_URL = awsConfig.api_url_get_menu;

const dayOptions = [
  { label: "Monday", value: 0 },
  { label: "Tuesday", value: 1 },
  { label: "Wednesday", value: 2 },
  { label: "Thursday", value: 3 },
  { label: "Friday", value: 4 },
  { label: "Saturday", value: 5 },
  { label: "Sunday", value: 6 },
];

const defaultClassDefinition = {
  classId: "",
  name: "Group fitness class",
  description: "",
  explicitDate: "",
  dayOfWeek: "1",
  times: "09:00",
  durationMinutes: "45",
  capacity: "8",
  price: "0.00",
};

const strengthOnCallTemplate = {
  id: "strength-on-call",
  name: "Strength on Call",
  description:
    "Two complimentary weekday fitness classes with recurring sessions, capacity, waitlist, cancellation rules and customer contact capture.",
  landingTitle: "Group fitness classes",
  shortDescription: "Choose a class and reserve your complimentary launch place.",
  bookingAheadWeeks: "8",
  timezone: "Europe/London",
  equipmentProvided: "Mats and resistance bands provided.",
  arrivalDetails: "Please arrive 10 minutes before the session and bring water.",
  cancellationPolicy:
    "If you can’t attend, please cancel at least 24 hours beforehand so your complimentary place can be offered to someone on the waitlist.",
  classDefinitions: [
    {
      classId: "bums-tums",
      name: "Bums & Tums",
      description:
        "45-minute lower-body and core-focused workout using bodyweight and resistance bands. Suitable for all fitness levels.",
      dayOfWeek: "1",
      times: "09:30",
      durationMinutes: "45",
      capacity: "8",
      price: "0.00",
    },
    {
      classId: "strength-core",
      name: "Strength & Core",
      description:
        "45-minute full-body session combining functional strength, resistance-band work and core training. Suitable for all abilities.",
      dayOfWeek: "4",
      times: "09:30",
      durationMinutes: "45",
      capacity: "8",
      price: "0.00",
    },
  ],
};

const blankBookingTemplate = {
  id: "blank",
  name: "Blank booking link",
  description: "Start from a clean draft and add your own recurring classes, pricing and customer guidance.",
};

const defaultFormState = {
  storeSlug: "fitness",
  trainerName: "",
  trainerEmail: "",
  instagramHandle: "",
  landingTitle: "Group fitness classes",
  shortDescription: "Choose a class and reserve your complimentary launch place.",
  location: "",
  timezone: "Europe/London",
  bookingAheadWeeks: "8",
  cancellationPolicy: "",
  arrivalDetails: "",
  equipmentProvided: "",
  heroImage: "",
  venueImage: "",
  venueName: "",
  mapUrl: "",
  launchNote: "",
  classDefinitions: [defaultClassDefinition],
};

function sanitizeSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

function deriveDefaultStoreSlug(business) {
  return sanitizeSlug(business?.slug || business?.name || "") || defaultFormState.storeSlug;
}

function parseRecurringTimes(raw) {
  return String(raw || "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

function buildClassId(name, index) {
  return sanitizeSlug(name) || `class-${index + 1}`;
}

function hasExplicitDate(definition) {
  return Boolean(String(definition?.date || definition?.explicitDate || "").trim());
}

function formatLaunchDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return raw;
  const [, year, month, day] = match;
  const monthIndex = Number(month) - 1;
  const monthLabels = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return `${monthLabels[monthIndex] || month} ${Number(day)}, ${year}`;
}

function normalizeClassDefinition(definition, index) {
  const times = parseRecurringTimes(definition.times);
  const normalized = {
    classId: buildClassId(definition.classId || definition.name, index),
    name: definition.name.trim() || `Class ${index + 1}`,
    description: definition.description.trim(),
    durationMinutes: Number(definition.durationMinutes || 45),
    capacity: Number(definition.capacity || 1),
    price: Number(definition.price || 0).toFixed(2),
  };
  const explicitDate = String(definition.explicitDate || "").trim();
  if (explicitDate) {
    return {
      ...normalized,
      date: explicitDate,
      time: times[0] || "09:00",
      times,
    };
  }
  return {
    ...normalized,
    daysOfWeek: [Number(definition.dayOfWeek || 5)],
    recurringTimes: times,
  };
}

export default function FitnessBookingTemplatePage({ business }) {
  const [selectedTemplate, setSelectedTemplate] = useState("blank");
  const [formState, setFormState] = useState(() => ({
    ...defaultFormState,
    trainerName: business?.name || "",
    trainerEmail: business?.email || "",
    storeSlug: deriveDefaultStoreSlug(business),
    location: business?.businessAddressFormatted || "",
    heroImage: business?.heroImage || (Array.isArray(business?.imageUrls) && business.imageUrls.length ? business.imageUrls[0] : ""),
    venueImage: business?.venueImage || "",
    venueName: business?.venueName || "",
    mapUrl: business?.mapUrl || "",
  }));
  const [publishes, setPublishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const [publishReadiness, setPublishReadiness] = useState(null);
  const [publishResult, setPublishResult] = useState(null);

  const businessLogoUrl = business?.logoUrl || "";
  const businessHeroFallback =
    formState.heroImage || (Array.isArray(business?.imageUrls) && business.imageUrls.length ? business.imageUrls[0] : "");
  const businessCurrencyCode = (business?.currencyCode || business?.currency || "GBP").toUpperCase();
  const businessContactEmail = business?.email || "";
  const businessContactPhone = business?.phone || "";

  const normalizedClasses = useMemo(
    () => formState.classDefinitions.map((definition, index) => normalizeClassDefinition(definition, index)),
    [formState.classDefinitions]
  );

  const allComplimentary = normalizedClasses.length > 0 && normalizedClasses.every((definition) => Number(definition.price || 0) <= 0);
  const launchDates = useMemo(
    () => Array.from(new Set(normalizedClasses.map((definition) => String(definition.date || "").trim()).filter(Boolean))).sort(),
    [normalizedClasses]
  );
  const defaultLaunchNote = useMemo(() => {
    if (!allComplimentary || !launchDates.length) return "";
    if (launchDates.length === 1) {
      return `Only the ${formatLaunchDate(launchDates[0])} launch sessions are complimentary. Future sessions on this page will be paid.`;
    }
    return "Only the listed launch sessions are complimentary. Future sessions on this page will be paid.";
  }, [allComplimentary, launchDates]);
  const launchNote = formState.launchNote.trim();
  const sessionIntro = useMemo(() => {
    return "Choose from the available sessions below. If a class is full, you can join the waitlist.";
  }, []);
  const publishPreview = useMemo(() => {
    const slug = (business?.slug || "your-brand").trim();
    return `https://${new URL(awsConfig.menu_base_url).host}/${slug}/${sanitizeSlug(formState.storeSlug) || "fitness"}`;
  }, [business?.slug, formState.storeSlug]);
  const templateReview = useMemo(
    () =>
      normalizedClasses.map((item) => {
        if (item.date) {
          return `${item.name} · ${item.date} · ${item.time || item.times?.[0] || "Time TBC"}`;
        }
        const dayLabel = dayOptions.find((option) => option.value === item.daysOfWeek?.[0])?.label || "Day";
        return `${item.name} · ${dayLabel}s · ${item.recurringTimes?.[0] || "Time TBC"}`;
      }),
    [normalizedClasses]
  );

  const getBearer = useCallback(async () => {
    const { tokens } = await fetchAuthSession();
    const bearer = resolveAuthToken(tokens);
    if (!bearer) throw new Error("Session expired. Please sign in again.");
    return bearer;
  }, []);

  const loadPublishes = useCallback(async () => {
    if (!business?.businessId) return;
    setLoading(true);
    setError("");
    try {
      const bearer = await getBearer();
      const response = await fetch(`${GET_MENU_API_URL}/${business.businessId}`, {
        headers: { Authorization: `Bearer ${bearer}` },
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body?.error || "Failed to load your published links.");
      const active = body?.publishes?.active || [];
      const filtered = active.filter(
        (entry) =>
          (entry.template || "").toLowerCase() === "booking" &&
          (entry.flowType || "").toLowerCase() === "fitness_booking"
      );
      setPublishes(filtered);
    } catch (err) {
      setError(err.message || "Failed to load your published links.");
    } finally {
      setLoading(false);
    }
  }, [business?.businessId, getBearer]);

  useEffect(() => {
    loadPublishes();
  }, [loadPublishes]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setFormState((current) => ({ ...current, [name]: value }));
  };

  const applyBlankTemplate = useCallback(() => {
    setSelectedTemplate(blankBookingTemplate.id);
    setFormState({
      ...defaultFormState,
      trainerName: business?.name || "",
      trainerEmail: business?.email || "",
      storeSlug: deriveDefaultStoreSlug(business),
      location: business?.businessAddressFormatted || "",
      timezone: business?.timeZone || defaultFormState.timezone,
      heroImage: business?.heroImage || (Array.isArray(business?.imageUrls) && business.imageUrls.length ? business.imageUrls[0] : ""),
      venueImage: business?.venueImage || "",
      venueName: business?.venueName || "",
      mapUrl: business?.mapUrl || "",
      launchNote: "",
    });
  }, [business]);

  const applyTemplate = useCallback(
    (template) => {
      setSelectedTemplate(template.id);
      setFormState((current) => ({
        ...current,
        landingTitle: template.landingTitle,
        shortDescription: template.shortDescription,
        bookingAheadWeeks: template.bookingAheadWeeks,
        timezone: business?.timeZone || template.timezone,
        equipmentProvided: template.equipmentProvided,
        arrivalDetails: template.arrivalDetails,
        cancellationPolicy: template.cancellationPolicy,
        classDefinitions: template.classDefinitions.map((definition) => ({ ...definition })),
      }));
    },
    [business?.timeZone]
  );

  const updateClassField = (index, field, value) => {
    setFormState((current) => ({
      ...current,
      classDefinitions: current.classDefinitions.map((definition, definitionIndex) =>
        definitionIndex === index ? { ...definition, [field]: value } : definition
      ),
    }));
  };

  const addClassDefinition = () => {
    setFormState((current) => ({
      ...current,
      classDefinitions: [
        ...current.classDefinitions,
        { ...defaultClassDefinition, name: `Class ${current.classDefinitions.length + 1}` },
      ],
    }));
  };

  const removeClassDefinition = (index) => {
    setFormState((current) => ({
      ...current,
      classDefinitions: current.classDefinitions.filter((_, definitionIndex) => definitionIndex !== index),
    }));
  };

  const publishFlow = async (event) => {
    event.preventDefault();
    setPublishing(true);
    setError("");
    setPublishReadiness(null);
    setPublishResult(null);
    try {
      const bearer = await getBearer();
      const classDefinitions = normalizedClasses;
      const primaryClass = classDefinitions[0];
      const payload = {
        template: "booking",
        storeSlug: sanitizeSlug(formState.storeSlug),
        paymentMethod: allComplimentary ? "none" : "stripe",
        items: [
          {
            id: "fitness-session",
            name: primaryClass?.name || "Fitness session",
            category: "Session",
            description: formState.shortDescription.trim(),
            price: primaryClass?.price || "0.00",
            published: true,
            isBooking: true,
            type: "booking",
          },
        ],
        bookingSettings: {
          slotDurationMinutes: Number(primaryClass?.durationMinutes || 45),
          maxSimultaneousBookings: Math.max(...classDefinitions.map((definition) => Number(definition.capacity || 1)), 1),
          timezone: formState.timezone.trim(),
          notes: formState.arrivalDetails.trim(),
          bookingAheadWeeks: Number(formState.bookingAheadWeeks || 8),
          cancellationPolicy: formState.cancellationPolicy.trim(),
          daysOfWeek: hasExplicitDate(primaryClass) ? [] : (primaryClass?.daysOfWeek || [5]),
        },
        templateMeta: {
          flowType: "fitness_booking",
          trainerName: formState.trainerName.trim(),
          trainerEmail: formState.trainerEmail.trim(),
          instagramHandle: formState.instagramHandle.trim(),
          sessionName: primaryClass?.name || formState.landingTitle.trim(),
          title: formState.landingTitle.trim(),
          shortDescription: formState.shortDescription.trim(),
          description: primaryClass?.description || formState.shortDescription.trim(),
          location: formState.location.trim(),
          currencyCode: businessCurrencyCode,
          capacity: Number(primaryClass?.capacity || 1),
          timezone: formState.timezone.trim(),
          bookingAheadWeeks: Number(formState.bookingAheadWeeks || 8),
          contactEmail: businessContactEmail,
          contactPhone: businessContactPhone,
          cancellationPolicy: formState.cancellationPolicy.trim(),
          arrivalDetails: formState.arrivalDetails.trim(),
          equipmentProvided: formState.equipmentProvided.trim(),
          heroImage: formState.heroImage.trim(),
          galleryImages: formState.heroImage.trim() ? [formState.heroImage.trim()] : [],
          venueImage: formState.venueImage.trim(),
          venueName: formState.venueName.trim(),
          mapUrl: formState.mapUrl.trim(),
          launchNote,
          sessionIntro,
          classDefinitions,
          requiredBookingFields: ["full_name", "email", "mobile"],
          poweredByLabel: "Powered by Layerbound",
        },
      };
      if (!hasExplicitDate(primaryClass)) {
        payload.templateMeta.recurringDay = Number(primaryClass?.daysOfWeek?.[0] ?? 5);
        payload.templateMeta.daysOfWeek = primaryClass?.daysOfWeek || [5];
        payload.templateMeta.recurringTimes = primaryClass?.recurringTimes || ["09:00"];
      }
      const response = await fetch(`${GET_MENU_API_URL}/${business.businessId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${bearer}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (body.publishReadiness) {
          setPublishReadiness(body.publishReadiness);
        }
        throw new Error(body.error || "Could not publish the booking link.");
      }
      setPublishResult(body);
      await loadPublishes();
    } catch (err) {
      setError(err.message || "Could not publish the booking link.");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="fitness-template-page">
      <div className="fitness-template-page__hero">
        <div className="fitness-template-page__hero-copy">
          <p className="fitness-template-page__eyebrow">Layerbound Booking Template</p>
          <h1>New booking link</h1>
          <p className="fitness-template-page__copy">
            Start with a blank booking link or use a reusable template, then review, customise and publish when it is ready.
          </p>
          <div className="fitness-template-page__hero-points">
            <span>One slug, multiple recurring classes</span>
            <span>{allComplimentary ? "Complimentary launch mode active" : "Stripe checkout stays active"}</span>
            <span>Future schedule updates keep the same public link</span>
          </div>
        </div>
        <Link className="fitness-template-page__back" to="/">
          Back to dashboard
        </Link>
      </div>

      <div className="fitness-template-page__grid">
        <section className="fitness-template-page__card">
          <h2>New booking link</h2>
          <p className="fitness-template-page__muted">
            Choose a blank draft or start from a reusable template. Nothing is published until you review and use the normal publish action below.
          </p>

          <div className={`fitness-template-page__class-card${selectedTemplate === "blank" ? " is-selected" : ""}`}>
            <div className="fitness-template-page__class-card-head">
              <div>
                <strong>{blankBookingTemplate.name}</strong>
                <p className="fitness-template-page__muted">{blankBookingTemplate.description}</p>
              </div>
              <button type="button" className="fitness-template-page__ghost-button" onClick={applyBlankTemplate}>
                Use blank booking link
              </button>
            </div>
          </div>

          <div className={`fitness-template-page__class-card${selectedTemplate === "strength-on-call" ? " is-selected" : ""}`}>
            <div className="fitness-template-page__class-card-head">
              <div>
                <strong>{strengthOnCallTemplate.name}</strong>
                <p className="fitness-template-page__muted">{strengthOnCallTemplate.description}</p>
              </div>
              <button type="button" className="fitness-template-page__ghost-button" onClick={() => applyTemplate(strengthOnCallTemplate)}>
                Use Strength on Call
              </button>
            </div>
          </div>

          <div className="fitness-template-page__brand-panel">
            <div>
              <span className="fitness-template-page__panel-label">Business branding</span>
              <strong>{business?.name || "Layerbound business"}</strong>
              <p>Logo and default business contact details are pulled from existing Layerbound settings.</p>
            </div>
            <div className="fitness-template-page__brand-mark">
              {businessLogoUrl ? <img src={businessLogoUrl} alt={`${business?.name || "Business"} logo`} /> : <span>LB</span>}
            </div>
          </div>

          <form id="fitness-booking-form" className="fitness-template-page__form" onSubmit={publishFlow}>
            <div className="fitness-template-page__result">
              <span>Template review</span>
              <strong>{selectedTemplate === "strength-on-call" ? "Strength on Call" : "Blank booking link"}</strong>
              <p><strong>Business</strong> {business?.name || "Selected business"}</p>
              <p><strong>Classes</strong> {templateReview.join(" / ")}</p>
              <p><strong>Capacity</strong> {Math.max(...normalizedClasses.map((item) => Number(item.capacity || 0)), 0)} per session</p>
              <p><strong>Price</strong> {allComplimentary ? "Complimentary" : "Paid"}</p>
              <p><strong>Waitlist</strong> On</p>
              <p><strong>Customer details</strong> Name · Email · Mobile</p>
              <div className="fitness-template-page__result-actions">
                <a href="#fitness-booking-form">Customise first</a>
              </div>
            </div>
            <label>
              Public slug
              <input name="storeSlug" value={formState.storeSlug} onChange={updateField} placeholder="fitness" />
            </label>

            <div className="fitness-template-page__form-row">
              <label>
                Trainer / business name
                <input name="trainerName" value={formState.trainerName} onChange={updateField} />
              </label>
              <label>
                Trainer email
                <input name="trainerEmail" value={formState.trainerEmail} onChange={updateField} placeholder="trainer@example.com" />
              </label>
              <label>
                Instagram handle
                <input name="instagramHandle" value={formState.instagramHandle} onChange={updateField} placeholder="@coachname" />
              </label>
            </div>

            <label>
              Landing title
              <input name="landingTitle" value={formState.landingTitle} onChange={updateField} />
            </label>
            <label>
              Public description
              <textarea name="shortDescription" value={formState.shortDescription} onChange={updateField} rows={4} />
            </label>
            <label>
              Hero image URL
              <input name="heroImage" value={formState.heroImage} onChange={updateField} placeholder="https://..." />
            </label>

            <div className="fitness-template-page__hero-preview">
              <div className="fitness-template-page__hero-preview-copy">
                <span className="fitness-template-page__panel-label">Hero image preview</span>
                <strong>Public page feature image</strong>
                <p>Uses the configured hero image. If left blank, Layerbound falls back to the first existing business image.</p>
              </div>
              <div className="fitness-template-page__hero-preview-frame">
                {businessHeroFallback ? (
                  <img src={businessHeroFallback} alt="Hero preview" />
                ) : (
                  <div className="fitness-template-page__hero-preview-empty">Add a strong portrait or session image</div>
                )}
              </div>
            </div>

            <div className="fitness-template-page__form-row">
              <label>
                Location
                <input name="location" value={formState.location} onChange={updateField} />
              </label>
              <label>
                Timezone
                <input name="timezone" value={formState.timezone} onChange={updateField} />
              </label>
              <label>
                Book-ahead weeks
                <input name="bookingAheadWeeks" value={formState.bookingAheadWeeks} onChange={updateField} />
              </label>
            </div>

            <label>
              Equipment provided
              <input name="equipmentProvided" value={formState.equipmentProvided} onChange={updateField} placeholder="Mats and resistance bands provided" />
            </label>
            <label>
              Arrival details
              <textarea name="arrivalDetails" value={formState.arrivalDetails} onChange={updateField} rows={3} />
            </label>
            <label>
              Cancellation policy
              <textarea name="cancellationPolicy" value={formState.cancellationPolicy} onChange={updateField} rows={3} />
            </label>
            <label>
              Launch note
              <textarea
                name="launchNote"
                value={formState.launchNote}
                onChange={updateField}
                rows={3}
                placeholder={defaultLaunchNote || "Optional launch note shown above the session list"}
              />
            </label>

            <div className="fitness-template-page__classes-header">
              <div>
                <span className="fitness-template-page__panel-label">Class definitions</span>
                <strong>Recurring classes on this link</strong>
              </div>
              <button type="button" className="fitness-template-page__ghost-button" onClick={addClassDefinition}>
                Add class
              </button>
            </div>

            <div className="fitness-template-page__classes">
              {formState.classDefinitions.map((definition, index) => (
                <article key={`${definition.classId || definition.name}-${index}`} className="fitness-template-page__class-card">
                  <div className="fitness-template-page__class-card-head">
                    <strong>Class {index + 1}</strong>
                    {formState.classDefinitions.length > 1 ? (
                      <button
                        type="button"
                        className="fitness-template-page__text-button"
                        onClick={() => removeClassDefinition(index)}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <label>
                    Class name
                    <input value={definition.name} onChange={(event) => updateClassField(index, "name", event.target.value)} />
                  </label>
                  <label>
                    Class description
                    <textarea
                      value={definition.description}
                      onChange={(event) => updateClassField(index, "description", event.target.value)}
                      rows={3}
                    />
                  </label>
                  <div className="fitness-template-page__form-row">
                    <label>
                      One-off date
                      <input
                        type="date"
                        value={definition.explicitDate || ""}
                        onChange={(event) => updateClassField(index, "explicitDate", event.target.value)}
                      />
                    </label>
                    <label>
                      Day
                      <select value={definition.dayOfWeek} onChange={(event) => updateClassField(index, "dayOfWeek", event.target.value)}>
                        {dayOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Times
                      <input
                        value={definition.times}
                        onChange={(event) => updateClassField(index, "times", event.target.value)}
                        placeholder="09:30, 18:00"
                      />
                    </label>
                  </div>
                  <p className="fitness-template-page__muted">
                    Add a one-off date for a single launch session. Leave it blank to keep this class on a recurring day-based schedule.
                  </p>
                  <div className="fitness-template-page__form-row">
                    <label>
                      Duration (mins)
                      <input
                        value={definition.durationMinutes}
                        onChange={(event) => updateClassField(index, "durationMinutes", event.target.value)}
                      />
                    </label>
                  </div>
                  <div className="fitness-template-page__form-row">
                    <label>
                      Capacity
                      <input value={definition.capacity} onChange={(event) => updateClassField(index, "capacity", event.target.value)} />
                    </label>
                    <label>
                      Price
                      <input value={definition.price} onChange={(event) => updateClassField(index, "price", event.target.value)} />
                    </label>
                    <label>
                      Currency
                      <input value={businessCurrencyCode} disabled readOnly />
                    </label>
                  </div>
                </article>
              ))}
            </div>

            <div className="fitness-template-page__result">
              <span>Checkout mode</span>
              <strong>{allComplimentary ? "Complimentary reservation flow" : "Stripe card checkout"}</strong>
            </div>

            <button type="submit" disabled={publishing}>
              {publishing ? "Publishing…" : "Create booking link"}
            </button>
          </form>

          {error ? <p className="fitness-template-page__error">{error}</p> : null}
          {publishReadiness?.blockers?.length ? (
            <div className="fitness-template-page__result">
              <span>Publish readiness</span>
              <strong>{publishReadiness.ready ? "Ready to publish" : `${publishReadiness.blockers.length} things to fix`}</strong>
              <p>{publishReadiness.blockers.join(" ")}</p>
              {publishReadiness.warnings?.length ? <p>{publishReadiness.warnings.join(" ")}</p> : null}
            </div>
          ) : null}
          <div className="fitness-template-page__preview">
            <span>Permanent link preview</span>
            <code>{publishPreview}</code>
          </div>
          {publishResult?.publicUrl ? (
            <div className="fitness-template-page__result">
              <span>Latest link</span>
              <a href={publishResult.publicUrl} target="_blank" rel="noreferrer">
                {publishResult.publicUrl}
              </a>
            </div>
          ) : null}
        </section>

        <section className="fitness-template-page__card">
          <h2>Live booking links</h2>
          <p className="fitness-template-page__muted">Existing fitness booking publishes for this Layerbound business.</p>
          <div className="fitness-template-page__result">
            <span>Default customer contact</span>
            <strong>{businessContactEmail || "No business email set"}</strong>
            <p>{businessContactPhone || "No business phone set"}</p>
          </div>
          {loading ? <p className="fitness-template-page__muted">Loading links…</p> : null}
          {!loading && !publishes.length ? <p className="fitness-template-page__muted">No live fitness booking links yet.</p> : null}
          <div className="fitness-template-page__links">
            {publishes.map((entry) => (
              <article key={entry.publishedAt} className="fitness-template-page__link-card">
                <div>
                  <strong>{entry.storeSlug || entry.storeId}</strong>
                  <p>Published {new Date(entry.publishedAt).toLocaleDateString()}</p>
                </div>
                <a href={entry.htmlUrl || entry.publicUrl} target="_blank" rel="noreferrer">
                  Open link
                </a>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
